import { useCallback, useMemo } from 'react';

import type {
  ModelLead,
  SalesFunnelStageFilterListDto,
} from '@app/api/Leads/types';
import type { SalesFunnelFilterDto } from '@app/api/SalesFunnels/types';
import {
  useAddMutation,
  useDeleteMutation,
  useExchangeMutation,
} from '@app/api/SalesFunnelStageLead';
import { useUserPrint } from '@app/hooks';
import { useReplaceLatestRegistryLead } from '@app/pages/Leads/model';
import type { LeadFunnelStageViewModel } from '@app/pages/Leads/ui/components/LeadFunnelStagesControl';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';

import {
  getAssignedStageFunnelId,
  getAssignedStageId,
  mapFunnelsToOptions,
  patchStageAdd,
  patchStageDelete,
  patchStageExchange,
} from '../utils';

interface UseLeadsRegistryFunnelStagesParams {
  lead: ModelLead;
  funnels: SalesFunnelFilterDto[];
}

export const useLeadsRegistryFunnelStages = ({
  lead,
  funnels,
}: UseLeadsRegistryFunnelStagesParams) => {
  const { params } = useUserPrint();
  const activeBranchId = params.ActiveBranchId;
  const { showNotification } = useNotification();
  const replaceLead = useReplaceLatestRegistryLead();
  const [addStage, { isLoading: isAdding }] = useAddMutation();
  const [exchangeStage, { isLoading: isExchanging }] = useExchangeMutation();
  const [deleteStage, { isLoading: isDeleting }] = useDeleteMutation();

  const options = useMemo(() => mapFunnelsToOptions(funnels), [funnels]);
  const optionByStageId = useMemo(
    () => new Map(options.map((option) => [option.stageId, option])),
    [options]
  );

  const stages = useMemo<LeadFunnelStageViewModel[]>(
    () =>
      (lead.FunnelStages ?? []).flatMap((stage) => {
        const stageId = getAssignedStageId(stage);

        if (stageId === null) return [];

        const option = optionByStageId.get(stageId);
        const funnelId = getAssignedStageFunnelId(stage, option);

        return (
          option ?? {
            funnelId: funnelId ?? stageId,
            funnelName: funnelId ? `Воронка ${funnelId}` : '',
            stageId,
            stageName: stage.Name || `Этап ${stageId}`,
            color: stage.Color,
          }
        );
      }),
    [lead.FunnelStages, optionByStageId]
  );

  const patchLeadStages = useCallback(
    (
      update: (
        stages: SalesFunnelStageFilterListDto[]
      ) => SalesFunnelStageFilterListDto[]
    ) => {
      replaceLead({
        ...lead,
        FunnelStages: update(lead.FunnelStages ?? []),
      });
    },
    [lead, replaceLead]
  );

  const addStageHandler = useCallback(
    async (stageId: number) => {
      const option = optionByStageId.get(stageId);
      if (!activeBranchId || !option) return;

      try {
        await addStage({
          ActiveBranchId: activeBranchId,
          leadId: lead.Id,
          funnelStageId: stageId,
        }).unwrap();

        patchLeadStages((currentStages) =>
          patchStageAdd(currentStages, option, optionByStageId)
        );
      } catch (error) {
        showNotification(error, 'warning');
      }
    },
    [
      activeBranchId,
      addStage,
      lead.Id,
      optionByStageId,
      patchLeadStages,
      showNotification,
    ]
  );

  const changeStageHandler = useCallback(
    async (currentStageId: number, nextStageId: number) => {
      const option = optionByStageId.get(nextStageId);
      if (!activeBranchId || !option || currentStageId === nextStageId) return;

      try {
        await exchangeStage({
          ActiveBranchId: activeBranchId,
          leadId: lead.Id,
          newFunnelStageId: nextStageId,
        }).unwrap();

        patchLeadStages((currentStages) =>
          patchStageExchange(currentStages, currentStageId, option)
        );
      } catch (error) {
        showNotification(error, 'warning');
      }
    },
    [
      activeBranchId,
      exchangeStage,
      lead.Id,
      optionByStageId,
      patchLeadStages,
      showNotification,
    ]
  );

  const deleteStageHandler = useCallback(
    async (stageId: number) => {
      if (!activeBranchId) return;

      try {
        await deleteStage({
          ActiveBranchId: activeBranchId,
          leadId: lead.Id,
          funnelStageId: stageId,
        }).unwrap();

        patchLeadStages((currentStages) =>
          patchStageDelete(currentStages, stageId)
        );
      } catch (error) {
        showNotification(error, 'warning');
      }
    },
    [activeBranchId, deleteStage, lead.Id, patchLeadStages, showNotification]
  );

  return {
    stages,
    options,
    isMutating: isAdding || isExchanging || isDeleting,
    addStage: addStageHandler,
    changeStage: changeStageHandler,
    deleteStage: deleteStageHandler,
  };
};
